import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { NavigationItem } from '../../types';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import trLogoWhite from '../../../assets/tr-logo-long-white.webp';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  const navigationItems: NavigationItem[] = [
    { id: 'technology', label: t('nav.technology'), href: '#technology', section: 'technology' },
    { id: 'products', label: t('nav.products'), href: '#products', section: 'products' },
    { id: 'research', label: t('nav.research'), href: '#research', section: 'research' },
    { id: 'career', label: t('nav.career'), href: '#career', section: 'career' },
    { id: 'about', label: t('nav.about'), href: '#about', section: 'about' },
    { id: 'contact', label: t('nav.contact'), href: '#contact', section: 'contact' },
  ];

  const scrollToSection = (sectionId: string) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = 64; // 4rem = 64px
        const elementPosition = element.offsetTop - headerHeight;
        
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }
    }, 100); // Delay to allow page to change
    setIsMenuOpen(false);
  };

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = navigationItems.map(item => item.section);
      const scrollPosition = window.scrollY + 100; // Offset for header

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Set initial active section

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-gray-800"
      role="banner"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={() => scrollToSection('hero')}
              className="text-lg sm:text-xl font-bold text-white hover:text-primary-blue transition-colors duration-200 focus:outline-none focus:ring-0 focus:ring-transparent active:outline-none active:ring-0 rounded-md px-2 py-1"
              style={{ outline: 'none', boxShadow: 'none' }}
            >
              <img src={trLogoWhite} alt="Tom Robotics Logo" width={160} height={40} className="h-8 w-auto" decoding="async" />
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <nav 
              className="flex items-center space-x-1"
              role="navigation"
              aria-label="Main navigation menu"
            >
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.section)}
                  className={`px-3 lg:px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-blue focus:ring-offset-2 focus:ring-offset-black min-h-[44px] ${
                    activeSection === item.section
                      ? 'text-white bg-primary-blue'
                      : 'text-gray-300 hover:text-white hover:bg-gray-800'
                  }`}
                  aria-current={activeSection === item.section ? 'page' : undefined}
                  aria-describedby={`nav-${item.id}-desc`}
                >
                  {item.label}
                  <span id={`nav-${item.id}-desc`} className="sr-only">
                    Navigate to {item.label} section
                  </span>
                </button>
              ))}
            </nav>
            <LanguageSwitcher />
          </div>

          {/* Mobile menu and language switcher */}
          <div className="md:hidden flex items-center space-x-2">
            <LanguageSwitcher />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-blue focus:ring-offset-2 focus:ring-offset-black transition-colors duration-200 p-2 rounded-md min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden" role="menu" aria-label="Mobile navigation menu">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-black border-t border-gray-800">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.section)}
                  className={`block w-full text-left px-3 py-3 text-base font-medium transition-colors duration-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-blue focus:ring-offset-2 focus:ring-offset-black min-h-[44px] ${
                    activeSection === item.section
                      ? 'text-white bg-primary-blue'
                      : 'text-gray-300 hover:text-white hover:bg-gray-800'
                  }`}
                  role="menuitem"
                  aria-current={activeSection === item.section ? 'page' : undefined}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;